/**
 * On-screen controls for phones and tablets.
 *
 * Nothing here moves the player directly: the stick and the buttons press the
 * same virtual keys the keyboard does, and a drag anywhere else on the screen
 * turns the view exactly like the mouse. So every mode — on foot, falling, in
 * the car, in the helicopter — behaves identically under either input, and the
 * controller stays the one place that knows what a key means.
 */

import type { PlayerMode } from '../config.js';
import type { PlayerController } from './PlayerController.js';

/** Stick deflection, 0–1, past which an axis counts as pressed. */
const AXIS_THRESHOLD = 0.35;
/** Deflection past which walking becomes running. */
const RUN_THRESHOLD = 0.85;
/** Touch drags are shorter than mouse sweeps, so they turn harder per pixel. */
const LOOK_GAIN = 1.8;
const STICK_RADIUS = 56;

interface ButtonSpec {
  label: string;
  code: string;
  title: string;
}

/** Which buttons each mode offers, beside the stick. */
const BUTTONS: Record<PlayerMode, ButtonSpec[]> = {
  walk: [
    { label: '⤒', code: 'Space', title: 'Jump' },
    { label: '🪂', code: 'KeyP', title: 'Parachute' },
    { label: '🚗', code: 'KeyE', title: 'Get into the vehicle or helicopter in reach' },
    { label: '🚁', code: 'KeyH', title: 'Helicopter' },
    { label: '👊', code: 'PunchRight', title: 'Punch; holding something, swing it' },
    { label: '✊', code: 'Grab', title: 'Pick up what lies ahead, or let it go' },
    { label: '👁', code: 'KeyC', title: 'Camera' },
  ],
  freefall: [
    { label: '🪂', code: 'KeyP', title: 'Open the canopy' },
    { label: '👁', code: 'KeyC', title: 'Camera' },
  ],
  drive: [
    { label: 'Brake', code: 'Space', title: 'Handbrake' },
    { label: '💡', code: 'ShiftLeft', title: 'Main beam on / off' },
    { label: '📯', code: 'KeyG', title: 'Hold to sound the horn' },
    { label: 'Exit', code: 'KeyE', title: 'Get out' },
    { label: '⛽', code: 'KeyF', title: 'Hold to fill up or charge' },
    { label: '📦', code: 'KeyY', title: 'Take the delivery job' },
    { label: '📻', code: 'KeyR', title: 'Radio: next station, then off' },
    { label: '👁', code: 'KeyC', title: 'Camera' },
  ],
  moto: [
    { label: 'Slide', code: 'Space', title: 'Rear brake: slide the back round' },
    { label: 'Jumps', code: 'KeyJ', title: 'Lay a line of jumps ahead' },
    { label: '📯', code: 'KeyG', title: 'Hold to sound the horn' },
    { label: 'Exit', code: 'KeyE', title: 'Get off' },
    { label: '⛽', code: 'KeyF', title: 'Hold to fill up' },
    { label: '👁', code: 'KeyC', title: 'Camera' },
  ],
  heli: [
    { label: '▲', code: 'Space', title: 'Climb' },
    { label: '▼', code: 'ShiftLeft', title: 'Descend' },
    { label: '⟲', code: 'KeyQ', title: 'Yaw left' },
    { label: '⟳', code: 'KeyE', title: 'Yaw right' },
    { label: 'Exit', code: 'KeyH', title: 'Get out' },
    { label: '⛽', code: 'KeyF', title: 'Hold to refuel on a helipad' },
    { label: '👁', code: 'KeyC', title: 'Camera' },
  ],
};

const CSS = `
html.wsg-touch, html.wsg-touch body, html.wsg-touch canvas { touch-action: none; -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; }
.wsg-stick { position: fixed; z-index: 11; left: max(24px, env(safe-area-inset-left)); bottom: max(34px, calc(env(safe-area-inset-bottom) + 24px));
  width: ${STICK_RADIUS * 2}px; height: ${STICK_RADIUS * 2}px; border-radius: 50%;
  background: rgba(247, 240, 227, 0.18); border: 2px solid rgba(247, 240, 227, 0.45); touch-action: none; }
.wsg-stick .knob { position: absolute; left: 50%; top: 50%; width: 52px; height: 52px; margin: -26px 0 0 -26px;
  border-radius: 50%; background: rgba(247, 240, 227, 0.85); box-shadow: 0 3px 10px rgba(30, 28, 40, 0.35); }
.wsg-buttons { position: fixed; z-index: 11; right: max(16px, env(safe-area-inset-right)); bottom: max(34px, calc(env(safe-area-inset-bottom) + 24px));
  display: grid; grid-template-columns: repeat(2, 58px); gap: 10px; direction: rtl; }
.wsg-buttons button { direction: ltr; width: 58px; height: 58px; border: 0; border-radius: 50%; padding: 0;
  background: rgba(247, 240, 227, 0.82); color: #2b2f36; font: 800 15px system-ui, sans-serif;
  box-shadow: 0 4px 12px rgba(30, 28, 40, 0.3); touch-action: none; }
.wsg-buttons button.held { background: #2f6fb0; color: #fff; }
html.wsg-touch .wsg-minimap { top: max(10px, env(safe-area-inset-top)); bottom: auto; right: max(10px, env(safe-area-inset-right));
  width: 134px; padding: 4px; }
html.wsg-touch .wsg-minimap .frame { width: 126px; height: 126px; }
html.wsg-touch .wsg-minimap .hint { display: none; }
html.wsg-touch .wsg-hud { max-width: calc(100vw - 170px); }
html.wsg-touch .wsg-hud pre { white-space: pre-wrap; }
`;

/** True on devices whose main pointer is a finger, or when `?touch=1` forces it. */
export function isTouchDevice(search: string): boolean {
  const forced = new URLSearchParams(search).get('touch');
  if (forced !== null) return forced !== '0';
  return window.matchMedia('(pointer: coarse)').matches;
}

export class TouchControls {
  private readonly stick = document.createElement('div');
  private readonly knob = document.createElement('div');
  private readonly buttons = document.createElement('div');
  private mode: PlayerMode | undefined;
  /** In the top-down view: zoom buttons, no stick. */
  private top = false;
  /** Virtual keys the stick is holding down right now. */
  private readonly stickKeys = new Set<string>();
  private visible = true;

  constructor(
    parent: HTMLElement,
    private readonly player: PlayerController,
    lookSurface: HTMLElement,
  ) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    document.documentElement.classList.add('wsg-touch');

    this.stick.className = 'wsg-stick';
    this.knob.className = 'knob';
    this.stick.appendChild(this.knob);
    this.buttons.className = 'wsg-buttons';
    parent.append(this.stick, this.buttons);

    this.bindStick();
    this.bindLook(lookSurface);
    // Stop long-presses opening the context menu over the controls.
    window.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** Hide while an overlay such as the world map has the screen. */
  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    this.buttons.style.display = visible ? '' : 'none';
    this.showStick();
  }

  /** From above a tap on the ground says where to go, so the stick is put away. */
  private showStick(): void {
    const shown = this.visible && !this.top;
    this.stick.style.display = shown ? '' : 'none';
    if (!shown) this.releaseStick();
  }

  /** Called every frame; rebuilds the buttons when the mode changes. */
  update(mode: PlayerMode): void {
    const top = this.player.topDown;
    if (mode === this.mode && top === this.top) return;
    if (top !== this.top) {
      this.top = top;
      this.showStick();
    }
    const wasWalking = this.mode === 'walk';
    this.mode = mode;
    // Run is the stick pushed hard on foot, but Shift is "descend" in the
    // helicopter, so it must not be carried across a mode change.
    if (wasWalking && this.stickKeys.delete('ShiftLeft')) this.player.virtualKey('ShiftLeft', false);
    // The helicopter is found on a pad and got into with E, unless vehicles are summoned.
    const specs = BUTTONS[mode].filter(spec => mode !== 'walk' || spec.code !== 'KeyH' || this.player.summonVehicles);
    // From above, zoom in and out in place of steering.
    if (top) specs.unshift({ label: '＋', code: 'TopZoomIn', title: 'Zoom in' }, { label: '－', code: 'TopZoomOut', title: 'Zoom out' });
    this.buttons.replaceChildren(...specs.map((spec) => this.button(spec)));
  }

  private button(spec: ButtonSpec): HTMLButtonElement {
    const el = document.createElement('button');
    el.textContent = spec.label;
    el.title = spec.title;
    el.setAttribute('aria-label', spec.title);
    const release = () => {
      if (!el.classList.contains('held')) return;
      el.classList.remove('held');
      this.player.virtualKey(spec.code, false);
    };
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      el.classList.add('held');
      this.player.virtualKey(spec.code, true);
    });
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);
    return el;
  }

  private bindStick(): void {
    let active: number | undefined;
    const move = (e: PointerEvent) => {
      const rect = this.stick.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);
      const length = Math.hypot(dx, dy);
      if (length > STICK_RADIUS) {
        dx *= STICK_RADIUS / length;
        dy *= STICK_RADIUS / length;
      }
      this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
      const x = dx / STICK_RADIUS;
      const y = dy / STICK_RADIUS;
      const wanted = new Set<string>();
      if (y < -AXIS_THRESHOLD) wanted.add('KeyW');
      if (y > AXIS_THRESHOLD) wanted.add('KeyS');
      if (x < -AXIS_THRESHOLD) wanted.add('KeyA');
      if (x > AXIS_THRESHOLD) wanted.add('KeyD');
      if (this.mode === 'walk' && Math.hypot(x, y) > RUN_THRESHOLD) wanted.add('ShiftLeft');
      this.setStickKeys(wanted);
    };
    this.stick.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      active = e.pointerId;
      this.stick.setPointerCapture(e.pointerId);
      move(e);
    });
    this.stick.addEventListener('pointermove', (e) => {
      if (e.pointerId === active) move(e);
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== active) return;
      active = undefined;
      this.releaseStick();
    };
    this.stick.addEventListener('pointerup', end);
    this.stick.addEventListener('pointercancel', end);
  }

  private setStickKeys(wanted: Set<string>): void {
    for (const code of this.stickKeys) {
      if (!wanted.has(code)) this.player.virtualKey(code, false);
    }
    for (const code of wanted) {
      if (!this.stickKeys.has(code)) this.player.virtualKey(code, true);
    }
    this.stickKeys.clear();
    for (const code of wanted) this.stickKeys.add(code);
  }

  private releaseStick(): void {
    this.knob.style.transform = '';
    this.setStickKeys(new Set());
  }

  /** A finger dragged on the world itself turns the camera. */
  private bindLook(surface: HTMLElement): void {
    const last = new Map<number, { x: number; y: number }>();
    surface.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      last.set(e.pointerId, { x: e.clientX, y: e.clientY });
    });
    surface.addEventListener('pointermove', (e) => {
      const prev = last.get(e.pointerId);
      if (prev === undefined) return;
      if (this.top) {
        // From above two fingers pinch the height; one finger does nothing but tap.
        if (last.size === 2) {
          const other = [...last.entries()].find(([id]) => id !== e.pointerId)?.[1];
          if (other !== undefined) {
            const before = Math.hypot(prev.x - other.x, prev.y - other.y);
            const after = Math.hypot(e.clientX - other.x, e.clientY - other.y);
            if (before > 10 && after > 10) this.player.zoomTop(before / after);
          }
        }
        prev.x = e.clientX;
        prev.y = e.clientY;
        return;
      }
      this.player.lookBy((e.clientX - prev.x) * LOOK_GAIN, (e.clientY - prev.y) * LOOK_GAIN);
      prev.x = e.clientX;
      prev.y = e.clientY;
    });
    const end = (e: PointerEvent) => { last.delete(e.pointerId); };
    surface.addEventListener('pointerup', end);
    surface.addEventListener('pointercancel', end);
  }
}
